/**
 * Resolve the embedding (parent) page's origin for postMessage targeting.
 *
 * We cannot simply trust `document.referrer`: after a Veriff `onReload`
 * `window.location.reload()`, the reloaded iframe's referrer becomes its OWN URL,
 * not the parent's. Using it would target IDME_READY at ourselves — the browser
 * blocks the postMessage, the parent never replies with IDME_INIT, and the flow
 * hangs on the spinner (observed in Safari).
 *
 * Resolution order — ignoring anything off the IDMe allowlist, and remembering
 * the winner so a later reload can recover it:
 *   1. `window.location.ancestorOrigins[0]` — the live parent-frame origin,
 *      unaffected by reload (Chrome/Safari; absent on Firefox). Authoritative,
 *      so it is trusted even when it equals our own origin (same-origin embed).
 *   2. a value persisted in sessionStorage on a prior load.
 *   3. `document.referrer` — correct on first load, self after a reload.
 *
 * The self-origin exclusion applies only to (2) and (3): those are the
 * reload-unsafe fallbacks where our own origin signals the reload false positive.
 */
import { getAllowedDomains } from "./apiConfig";

const PARENT_ORIGIN_STORAGE_KEY = "idme_parent_origin";

export function isAllowedOrigin(origin: string): boolean {
  try {
    const { hostname } = new URL(origin);
    return getAllowedDomains().some((domain) => hostname === domain || hostname.endsWith(`.${domain}`));
  } catch {
    return false;
  }
}

function readStored(): string | null {
  try {
    return window.sessionStorage.getItem(PARENT_ORIGIN_STORAGE_KEY);
  } catch {
    return null;
  }
}

function persist(origin: string): void {
  try {
    window.sessionStorage.setItem(PARENT_ORIGIN_STORAGE_KEY, origin);
  } catch {
    // sessionStorage can be unavailable (private mode / partitioned storage) — best-effort.
  }
}

/**
 * Best-effort resolution of the parent origin. Returns the first allowlisted,
 * non-self candidate (and persists it), or null when none is available.
 */
export function resolveParentOrigin(): string | null {
  if (typeof window === "undefined") return null;
  const selfOrigin = window.location.origin;

  let ancestor: string | null = null;
  try {
    ancestor = window.location.ancestorOrigins?.[0] ?? null;
  } catch {
    ancestor = null;
  }

  // ancestorOrigins[0] is the live parent-frame origin and is unaffected by a
  // reload, so it is authoritative even when it equals our own origin — a
  // legitimate same-origin embed (e.g. the /testing page, where the host and the
  // iframe are both on websdk.idme.cc). Trust it directly; the self-origin
  // exclusion below is only needed for the reload-unsafe referrer fallback.
  if (ancestor && isAllowedOrigin(ancestor)) {
    persist(ancestor);
    return ancestor;
  }

  let referrer: string | null = null;
  try {
    referrer = document.referrer ? new URL(document.referrer).origin : null;
  } catch {
    referrer = null;
  }

  // Fallbacks for browsers without ancestorOrigins (Firefox). These MUST exclude
  // our own origin: after a Veriff onReload window.location.reload() the reloaded
  // iframe's referrer becomes its OWN origin, and trusting it would target
  // IDME_READY at ourselves. A prior load's persisted parent recovers that case.
  for (const candidate of [readStored(), referrer]) {
    if (candidate && candidate !== selfOrigin && isAllowedOrigin(candidate)) {
      persist(candidate);
      return candidate;
    }
  }
  return null;
}
